import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import {
  EReviewSubmissionStatus,
  type TReviewMissingCounts,
  type TReviewQueueQuery,
  type TReviewQueueResponse,
} from '@onesource/schemas';
import { FC } from 'react';

import { REVIEW_STATUS_STYLE } from './review-status-chip';
import { URGENCY_STYLE } from './review-urgency';

/** A submissions tile, or the students who have not submitted. */
export type TReviewView = TReviewQueueQuery['status'] | 'not_submitted';

export const NOT_SUBMITTED_COLOR = '#6d28d9';

const TILES: Array<{ value: TReviewQueueQuery['status']; label: string; hint: string }> = [
  { value: EReviewSubmissionStatus.waiting, label: 'Waiting', hint: 'Need your feedback' },
  { value: EReviewSubmissionStatus.in_progress, label: 'Drafts', hint: 'Feedback not sent yet' },
  { value: EReviewSubmissionStatus.reviewed, label: 'Reviewed', hint: 'Feedback shared' },
  { value: EReviewSubmissionStatus.rejected, label: 'Rejected', hint: 'Sent back to resubmit' },
  { value: 'all', label: 'All', hint: 'Every submission' },
];

const tileColor = (value: TReviewQueueQuery['status']) =>
  value === 'all' ? '#3f3f46' : REVIEW_STATUS_STYLE[value].color;

interface ReviewStatusTilesProps {
  value: TReviewView;
  counts: TReviewQueueResponse['counts'] | undefined;
  overdueReviews: TReviewQueueResponse['overdueReviews'] | undefined;
  notSubmitted: TReviewMissingCounts | undefined;
  onChange: (value: TReviewView) => void;
}

interface TileProps {
  label: string;
  count: number | undefined;
  hint: string;
  /** Overdue count; replaces the hint, in red, when above 0. */
  overdue?: number;
  color: string;
  isActive: boolean;
  onClick: () => void;
}

const Tile: FC<TileProps> = ({ label, count, hint, overdue = 0, color, isActive, onClick }) => (
  <ButtonBase
    aria-pressed={isActive}
    onClick={onClick}
    sx={{
      display: 'block',
      textAlign: 'left',
      p: 1.75,
      borderRadius: '12px',
      bgcolor: 'common.white',
      border: '1px solid',
      borderColor: isActive ? color : 'divider',
      boxShadow: isActive ? `inset 0 0 0 1px ${color}` : 'none',
      transition: 'border-color 120ms ease-out, box-shadow 120ms ease-out',
      '&:hover': { borderColor: color },
    }}
  >
    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: isActive ? color : 'text.secondary' }}>
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: '1.625rem',
        fontWeight: 700,
        lineHeight: 1.25,
        color: 'text.primary',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {count ?? '–'}
    </Typography>
    {overdue > 0 ? (
      <Typography variant="caption" noWrap display="block" sx={{ color: URGENCY_STYLE.overdue.text, fontWeight: 700 }}>
        {overdue} overdue
      </Typography>
    ) : (
      <Typography variant="caption" color="text.secondary" noWrap display="block">
        {hint}
      </Typography>
    )}
  </ButtonBase>
);

/**
 * The queue's status filter, as count tiles — the teacher sees how much is waiting
 * at a glance. The last tile counts students, not submissions: who still has a
 * form to send in.
 */
export const ReviewStatusTiles: FC<ReviewStatusTilesProps> = ({
  value,
  counts,
  overdueReviews,
  notSubmitted,
  onChange,
}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' },
      gap: 1.5,
    }}
  >
    {TILES.map(tile => (
      <Tile
        key={tile.value}
        label={tile.label}
        count={counts?.[tile.value]}
        hint={tile.hint}
        overdue={
          tile.value === EReviewSubmissionStatus.waiting || tile.value === EReviewSubmissionStatus.in_progress
            ? overdueReviews?.[tile.value]
            : undefined
        }
        color={tileColor(tile.value)}
        isActive={tile.value === value}
        onClick={() => onChange(tile.value)}
      />
    ))}
    <Tile
      label="Not submitted"
      count={notSubmitted?.students}
      hint={
        notSubmitted
          ? `${notSubmitted.forms} ${notSubmitted.forms === 1 ? 'form' : 'forms'} missing`
          : 'Students behind'
      }
      overdue={notSubmitted?.overdueStudents}
      color={NOT_SUBMITTED_COLOR}
      isActive={value === 'not_submitted'}
      onClick={() => onChange('not_submitted')}
    />
  </Box>
);
